import { useRef } from "react";
import { Bold, Italic, List } from "lucide-react";
import AutoTextarea from "@/components/AutoTextarea";

export default function RichTextarea({ value, onChange, testid, ...props }) {
  const inputRef = useRef(null);

  const fire = (next, selStart, selEnd) => {
    onChange({ target: { value: next } });
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el && selStart != null) {
        el.focus();
        el.setSelectionRange(selStart, selEnd);
      }
    });
  };

  const toggleWrap = (marker) => {
    const el = inputRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const sel = value.slice(s, e);
    if (sel.length > marker.length * 2 - 1 && sel.startsWith(marker) && sel.endsWith(marker)) {
      fire(value.slice(0, s) + sel.slice(marker.length, -marker.length) + value.slice(e), s, e - marker.length * 2);
    } else {
      const inner = sel || "text";
      fire(value.slice(0, s) + marker + inner + marker + value.slice(e), s + marker.length, s + marker.length + inner.length);
    }
  };

  const toggleList = () => {
    const el = inputRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e } = el;
    const lineStart = value.lastIndexOf("\n", s - 1) + 1;
    const lines = value.slice(lineStart, e).split("\n");
    const allBulleted = lines.every((l) => l.startsWith("- "));
    const next = lines.map((l) => (allBulleted ? l.replace(/^- /, "") : `- ${l}`)).join("\n");
    fire(value.slice(0, lineStart) + next + value.slice(e));
  };

  const tools = [
    ["bold", <Bold size={14} />, () => toggleWrap("**")],
    ["italic", <Italic size={14} />, () => toggleWrap("*")],
    ["list", <List size={14} />, toggleList],
  ];

  return (
    <div>
      <div className="flex gap-2 mb-3">
        {tools.map(([key, icon, fn]) => (
          <button
            key={key}
            type="button"
            data-testid={`${testid}-${key}-button`}
            onClick={fn}
            className="border border-white/20 hover:border-accent hover:text-accent text-white/70 p-2 transition-colors duration-300"
          >
            {icon}
          </button>
        ))}
      </div>
      <AutoTextarea inputRef={inputRef} value={value} onChange={onChange} data-testid={testid} {...props} />
    </div>
  );
}
